import { useState, useEffect } from "react";
import { HelpCircle, X, CheckCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useGetFacilities } from "../../hooks/appointmentServiceHook";
import { useFacilityStore } from "../../store/useFacilityStore";

const ContactButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const[selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null);
  const primaryColor = "#3b82f6";
  const{facilities}=useGetFacilities()
  const{currentFacility}=useFacilityStore()

useEffect(() => {
  const savedFacilityId = localStorage?.getItem("selectedFacilityId");
  if (savedFacilityId) {
    setSelectedFacilityId(Number(savedFacilityId));
  } else if (currentFacility && currentFacility.id) {
    setSelectedFacilityId(Number(currentFacility.id));
  }
}, [currentFacility]);

const selectedFacility = facilities?.find(
  (facility) => Number(facility.id) === selectedFacilityId
);

// Fallback: if selectedFacility is not found, but currentFacility exists, use its info
const defaultPhoneNumbers = ["+251936010720", "+251938958554"];
const fallbackFacility = selectedFacility
  ? selectedFacility
  : (currentFacility && currentFacility.id && currentFacility.name)
    ? {
        id: currentFacility.id,
        facilityName: currentFacility.name,
        phoneNumber: currentFacility.phoneNumber || "",
        address: currentFacility.address || "",
      }
    : null;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div className="fixed bottom-20 right-4 z-50">
      <AnimatePresence>
        {isOpen && (
          (fallbackFacility && fallbackFacility.phoneNumber && fallbackFacility.phoneNumber !== "-" && fallbackFacility.phoneNumber !== "_") ? (
            <motion.button
              whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                window.location.href = `tel:${fallbackFacility.phoneNumber}`;
                if (navigator.vibrate) {
                  navigator.vibrate(50);
                }
              }}
              className={`flex items-center gap-2 py-3 px-5 rounded-2xl shadow-lg w-full mb-4 ${isMobile ? "text-base font-medium" : ""} bg-green-500 text-white`}
              aria-label={`Call ${fallbackFacility.phoneNumber}`}
            >
              <span className="mr-4">{fallbackFacility.phoneNumber}</span>
              <span className="ml-auto flex items-center">
                <div className="flex items-center gap-1">
                  <span className="animate-pulse w-2 h-2 bg-green-300 rounded-full"></span>
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </span>
            </motion.button>
          ) : (
            <div className="flex flex-col gap-3 mb-4">
              {defaultPhoneNumbers.map((num) => (
                <motion.button
                  key={num}
                  whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    window.location.href = `tel:${num}`;
                    if (navigator.vibrate) {
                      navigator.vibrate(50);
                    }
                  }}
                  className={`flex items-center gap-2 py-3 px-5 rounded-2xl shadow-lg w-full bg-green-500 text-white`}
                  aria-label={`Call ${num}`}
                >
                  <span className="mr-4">{num}</span>
                  <span className="ml-auto flex items-center">
                    <div className="flex items-center gap-1">
                      <span className="animate-pulse w-2 h-2 bg-green-300 rounded-full"></span>
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </span>
                </motion.button>
              ))}
            </div>
          )
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setIsOpen((prev) => !prev);
          if (navigator.vibrate) {
            navigator.vibrate(isOpen ? 20 : 50);
          }
        }}
        style={{ backgroundColor: isOpen ? "#f43f5e" : primaryColor }}
        className={`flex items-center gap-2 px-5 py-3.5 rounded-full shadow-lg text-white ${
          isMobile ? "fixed bottom-6 right-4 text-base font-medium" : ""
        }`}
        aria-label={isOpen ? "Close contact options" : "Call for help"}
      >
        {isOpen ? (
          <>
            <X className="w-5 h-5" />
            <span className="font-medium">Close</span>
          </>
        ) : (
          <>
            <HelpCircle className="w-5 h-5" />
            <span className="font-medium">Call for Help</span>
          </>
        )}
      </motion.button>
    </div>
  );
};

export default ContactButton;
